import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../../components/ui/card.js";
import { GRAMMAR_CATEGORIES, GRAMMAR_TOPICS, type GrammarTopic } from "../../data/grammar.js";
import { GRAMMAR_EXERCISES } from "../../data/grammar-exercises.js";
import { GrammarQuiz } from "./GrammarQuiz.js";

const LEVEL_STYLES: Record<GrammarTopic["level"], string> = {
  A1: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
  A2: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
  B1: "border-amber-400/30 bg-amber-400/10 text-amber-200",
  B2: "border-amber-400/30 bg-amber-400/10 text-amber-200",
  C1: "border-rose-400/30 bg-rose-400/10 text-rose-200",
};

export function GrammarPage() {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(GRAMMAR_TOPICS[0]!.id);
  const selected = GRAMMAR_TOPICS.find((topic) => topic.id === selectedId) ?? GRAMMAR_TOPICS[0]!;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
          {t("grammar.title")}
        </h1>
        <p className="mt-1 text-[var(--text-secondary)]">{t("grammar.subtitle")}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-[280px_1fr]">
        <Card className="h-fit max-h-[75vh] space-y-4 overflow-y-auto md:sticky md:top-20">
          {GRAMMAR_CATEGORIES.map((category) => (
            <div key={category.id}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                {category.title}
              </p>
              <div className="space-y-1">
                {GRAMMAR_TOPICS.filter((topic) => topic.categoryId === category.id).map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => setSelectedId(topic.id)}
                    className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      topic.id === selectedId
                        ? "bg-[var(--primary)]/12 text-[var(--accent)]"
                        : "text-[var(--text-secondary)] hover:bg-white/5 hover:text-[var(--text-primary)]"
                    }`}
                  >
                    {topic.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </Card>

        <Card className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-xl font-semibold text-[var(--text-primary)]">
              {selected.title}
            </h2>
            <span
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${LEVEL_STYLES[selected.level]}`}
            >
              {selected.level}
            </span>
          </div>
          <p className="text-[var(--text-secondary)]">{selected.summary}</p>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
              {t("grammar.pointsLabel")}
            </p>
            <ul className="space-y-1.5">
              {selected.points.map((point, i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--text-primary)]">
                  <span className="text-[var(--text-muted)]">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
              {t("grammar.examplesLabel")}
            </p>
            <div className="space-y-2">
              {selected.examples.map((example, i) => (
                <div key={i} className="rounded-lg border border-[var(--glass-border)] px-3 py-2">
                  <p className="text-sm text-[var(--text-primary)]">{example.en}</p>
                  <p className="mt-0.5 text-xs text-[var(--text-muted)]">{example.vi}</p>
                </div>
              ))}
            </div>
          </div>

          {GRAMMAR_EXERCISES[selected.id]?.length ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                {t("grammar.exercisesLabel")}
              </p>
              <GrammarQuiz key={selected.id} exercises={GRAMMAR_EXERCISES[selected.id]!} />
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
